import { type InferOutput, array, boolean, file, nullish, number, object, string } from "valibot"

import { BaseApiService, KyOptions } from "./api.service"

// For now we are only using /marketing endpoint from this one,
// TODO: add other endpoints

export const userFileSchema = nullish(
  object({
    id: nullish(number()),
    file: nullish(file()),
    url: nullish(string()),
  })
)
export type UserFileParams = InferOutput<typeof userFileSchema>

export const documentsUploadSchema = object({
  documentType: string(),
  files: array(file()),
  documentId: nullish(number()),
})
export type UploadDocumentParams = InferOutput<typeof documentsUploadSchema>

export const removeDocumentSchema = object({
  fileId: number(),
  documentId: number(),
})

export type RemoveDocumentParams = InferOutput<typeof removeDocumentSchema>

export const documentTypeSchema = object({
  id: string(),
  descriptionHe: string(),
  descriptionEn: string(),
  group: string(),
  subGroup: string(),
})

export type DocumentType = InferOutput<typeof documentTypeSchema>

export const documentDetailSchema = object({
  id: nullish(number()),
  name: nullish(string()),
  url: nullish(string()),
})

export const userDocumentSchema = object({
  id: number(),
  documentType: string(),
  group: string(),
  subGroup: string(),
  approved: nullish(boolean()),
  partnerId: nullish(number()),
  updateDate: nullish(number()),
  details: array(documentDetailSchema),
})

export type UserDocument = InferOutput<typeof userDocumentSchema>

class DocumentsServiceRaw extends BaseApiService {
  /** @example { params: { group: "PERSONAL", subGroup: "PERSONALID" } } */
  getTypes = async (config: KyOptions = {}) => {
    return this.instance
      .get("/documents", config)
      .then(this.getData)
      .then((res) => this.parse(res, array(documentTypeSchema)))
  }

  /** @example { params: { documentType: "BIOID", group: "PERSONAL", subGroup: "PERSONALID" } } */
  getUserDocuments = async (config: KyOptions = {}) => {
    return this.instance
      .get("/users/documents", config)
      .then(this.getData)
      .then((res) => this.parse(res, array(userDocumentSchema)))
  }

  /** @example { params: { documentType: "BIOID", group: "PERSONAL", subGroup: "PERSONALID" } } */
  uploadDocuments = async (data: UploadDocumentParams, config: KyOptions = {}) => {
    const { documentType, files, documentId } = data ?? {}

    if (!documentType) throw new Error("documentType is required")
    else if (!files || !files.length) throw new Error("files is required")

    const formData = new FormData()

    // Assigning the documentType value
    formData.append("documentType", documentType)

    for (const file of Array.isArray(files) ? files : [files]) {
      formData.append("files", file as any)
    }

    return this.instance
      .post(`/users/documents${documentId ? `/${documentId}/files` : ""}`, undefined, {
        body: formData,
        ...config,
      })
      .then(this.getData)
      .then((res) => this.parse(res, userDocumentSchema))
  }

  removeFile = async (config: KyOptions<RemoveDocumentParams> = {}) => {
    const { documentId, fileId } = config.params ?? {}
    if (!documentId) throw new Error("documentId is required")
    else if (!fileId) throw new Error("fileId is required")

    const res = await this.instance.delete(`/users/documents/${documentId}/files/${fileId}`, config)

    return this.getData(res)
  }

  downloadDocument = async (fileId: number) => {
    if (!fileId) throw new Error("fileId is required")

    return this.instance.get("/users/download/" + fileId, { responseType: "blob" })
  }

  downloadAndCreateBlob = async (id: number) => {
    const res = await this.downloadDocument(id)

    return { id, url: URL.createObjectURL(res.data) }
  }

  generateContract = async (config: KyOptions = {}) => {
    const res = await this.instance.post(
      "/users/documents/generatecontracts",
      { fileName: "finq_agreement" },
      { responseType: "blob" }
    )

    return res?.data?.url || null
  }

  generateSignedContract = async (signature: string, config: KyOptions = {}) => {
    const res = await this.instance.post(
      "/users/documents/generatecontracts",
      { fileName: "finq_signed", signature: signature },
      { responseType: "blob" }
    )

    return this.getData(res)
  }

  approveDocument = async (documentId: number, config: KyOptions = {}) => {
    const res = await this.instance.put("/users/documents/" + documentId, { approved: true }, config)

    return this.getData(res)
  }

  // Agreements apis
  getAgreements = async (config: KyOptions = {}) => {
    const res = await this.instance.get("/users/agreements", config)

    return this.getData(res)
  }

  approveAgreement = async (agreementId: number, data?: any, config: KyOptions = {}) => {
    const res = await this.instance.post("/users/agreements/" + agreementId + "/approve", data, config)

    return this.getData(res)
  }

  downloadAgreement = async (agreementName: string, config: KyOptions = {}) => {
    return this.instance.get("/users/agreements/download-pdf/" + agreementName, {
      responseType: "blob",
      ...config,
    })
  }
}

/** Exporting a singleton object to entire app */
export const DocumentsService = new DocumentsServiceRaw()
